import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { DayView } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useLibraryStore } from "../../store/libraryStore";
import { computeDayViews } from "../../utils/dayViews";
import { blockStartTime } from "../../utils/dayViews";
import {
  DEFAULT_SCROLL_HOUR,
  ZOOM_LEVELS_PX_PER_HOUR,
  columnHeightPx,
  instantToY,
} from "../../utils/timeGrid";
import { buildAccommodationContext } from "./accommodationContext";
import { CalendarDayHeader } from "./CalendarDayHeader";
import { CalendarDayColumn } from "./CalendarDayColumn";
import { AccommodationBanner } from "./AccommodationBanner";
import { TimeAxis } from "./TimeAxis";
import { WeekAllDayArea } from "./WeekAllDayArea";
import { AddLegButton } from "./CalendarLegBand";
import { useLegStore } from "../../store/legStore";
import { useCalendarPxPerMinute, useUIStore } from "../../store/uiStore";
import { Button } from "../Button";
import { useNow } from "../../hooks/useNow";
import { findTodayView, nowInZone, nowTimeLabel } from "../../utils/nowTime";
import type { DayAccommodationContext } from "./types";

/** Sticky hour gutter width plus the gap before the first column. */
const TIME_GUTTER_PX = 56 + 10;

/**
 * The week-style trip grid: every trip day as a side-by-side column over a
 * shared vertical hour axis, with day headers pinned to the top and
 * accommodation banners pinned to the bottom. Read rendering — editing
 * gestures arrive with milestone 3.
 */
interface CalendarWeekViewProps {
  onShowOnMap?: (poiId: string) => void;
  dayViews?: DayView[];
  contextKey?: string;
}

export function CalendarWeekView({
  onShowOnMap,
  dayViews: selectedDayViews,
  contextKey,
}: CalendarWeekViewProps) {
  const trip = useTripStore((s) => s.trip);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const getAccommodation = useLibraryStore((s) => s.getAccommodation);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const pxPerMinute = useCalendarPxPerMinute();
  const hourHeight = pxPerMinute * 60;
  const zoomIndex = useUIStore((s) => s.calendarZoomIndex);
  const zoomCalendarIn = useUIStore((s) => s.zoomCalendarIn);
  const zoomCalendarOut = useUIStore((s) => s.zoomCalendarOut);

  const allDayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const dayViews = selectedDayViews ?? allDayViews;

  const now = useNow();
  const todayView = useMemo(
    () => findTodayView(dayViews, now),
    [dayViews, now],
  );
  const nowMarker = todayView
    ? {
        top: instantToY(
          nowInZone(now, todayView.zone),
          todayView.date,
          todayView.zone,
          pxPerMinute,
        ),
        label: nowTimeLabel(now, todayView.zone),
      }
    : null;

  const accommodationByDate = useMemo(() => {
    const contexts = new Map<string, DayAccommodationContext>();
    for (const dayView of dayViews) {
      contexts.set(
        dayView.date,
        buildAccommodationContext(dayView.date, schedule, getAccommodation),
      );
    }
    return contexts;
  }, [dayViews, schedule, getAccommodation]);

  const maxColumnHeight = useMemo(
    () =>
      dayViews.reduce(
        (max, dayView) =>
          Math.max(
            max,
            columnHeightPx(dayView.date, dayView.zone, pxPerMinute),
          ),
        24 * hourHeight,
      ),
    [dayViews, pxPerMinute, hourHeight],
  );
  const hasAllDayNotes = dayViews.some(
    (dayView) => dayView.allDayBlocks.length > 0,
  );
  // The band shows whenever a leg touches these dates, and stays available to
  // editors even with no legs yet so the "add leg" control is reachable.
  const legs = useLegStore((state) => state.legs);
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;
  const showLegBand =
    !isReadOnly() ||
    legs.some(
      (leg) =>
        firstDate &&
        lastDate &&
        leg.startDate <= lastDate &&
        leg.endDate >= firstDate,
    );

  // Initial scroll: open at the current time while today is on screen,
  // otherwise anchor to the earliest block, fallback 08:00
  useEffect(() => {
    const scroller = scrollRef.current;
    const body = bodyRef.current;
    if (!scroller || !body) return;

    if (todayView && nowMarker) {
      scroller.scrollTop = Math.max(
        body.offsetTop + nowMarker.top - scroller.clientHeight / 3,
        0,
      );
      // Bring today's column in beside the sticky time gutter
      const column = scroller.querySelector<HTMLElement>(
        `[data-calendar-date="${todayView.date}"]`,
      );
      if (
        column &&
        column.offsetLeft + column.offsetWidth > scroller.clientWidth
      ) {
        scroller.scrollLeft = Math.max(column.offsetLeft - TIME_GUTTER_PX, 0);
      }
      return;
    }

    let anchorY = DEFAULT_SCROLL_HOUR * hourHeight;
    for (const dayView of dayViews) {
      const first = dayView.blocks[0];
      if (!first) continue;
      const y = instantToY(
        blockStartTime(first),
        dayView.date,
        dayView.zone,
        pxPerMinute,
      );
      anchorY = Math.min(anchorY, Math.max(y, 0));
    }
    scroller.scrollTop = Math.max(body.offsetTop + anchorY - 16, 0);
    // Re-anchor only when switching trips — not on every schedule edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id, contextKey]);

  // Zoom: keep the time at the centre of the viewport where it was. The
  // body's offsetTop (sticky header) doesn't scale, so only the grid portion
  // of the scroll offset is rescaled.
  const previousPxPerMinute = useRef(pxPerMinute);
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const body = bodyRef.current;
    const previous = previousPxPerMinute.current;
    previousPxPerMinute.current = pxPerMinute;
    if (!scroller || !body || previous === pxPerMinute) return;

    const centre = scroller.scrollTop + scroller.clientHeight / 2;
    const gridY = Math.max(centre - body.offsetTop, 0);
    const nextGridY = (gridY / previous) * pxPerMinute;
    scroller.scrollTop = Math.max(
      body.offsetTop + nextGridY - scroller.clientHeight / 2,
      0,
    );
  }, [pxPerMinute]);

  if (!trip) return null;

  return (
    <div
      ref={scrollRef}
      className="custom-scrollbar relative flex-1 scroll-pl-16 overflow-auto bg-page max-md:snap-x max-md:snap-proximity"
      data-testid="calendar-week-view"
    >
      <div
        className="relative grid gap-x-2.5 px-4"
        style={{
          gridTemplateColumns: `56px repeat(${dayViews.length}, minmax(208px, 1fr))`,
          minWidth: 56 + dayViews.length * 216,
        }}
      >
        {/* Header + all-day ranges share one sticky surface so ranges align. */}
        <div
          className="sticky top-0 z-20 grid gap-x-2.5 bg-page pb-2 pt-3"
          style={{
            gridColumn: "1 / -1",
            gridTemplateColumns: `56px repeat(${dayViews.length}, minmax(208px, 1fr))`,
          }}
        >
          <div />
          {dayViews.map((dayView, index) => (
            <CalendarDayHeader
              key={dayView.date}
              date={dayView.date}
              dayIndex={index}
              zone={dayView.zone}
              defaultTimezone={trip.defaultTimezone}
              isReadOnly={isReadOnly()}
            />
          ))}
          {(hasAllDayNotes || showLegBand) && (
            <>
              <div className="sticky left-0 z-20 flex flex-col items-end gap-1 bg-page px-1 pt-2">
                <span className="text-2xs font-extrabold uppercase tracking-wider text-tertiary">
                  All day
                </span>
                {showLegBand && !isReadOnly() && (
                  <AddLegButton dayViews={dayViews} />
                )}
              </div>
              <div
                className="pt-2"
                style={{ gridColumn: `2 / ${dayViews.length + 2}` }}
              >
                {/* Legs and all-day notes share lanes; legs take the top. */}
                <WeekAllDayArea
                  dayViews={dayViews}
                  showLegBand={showLegBand}
                  isReadOnly={isReadOnly()}
                />
              </div>
            </>
          )}
        </div>

        {/* Body row: time gutter + day canvases */}
        <div ref={bodyRef} />
        {dayViews.map((dayView) => (
          <CalendarDayColumn
            key={dayView.date}
            dayView={dayView}
            accommodation={accommodationByDate.get(dayView.date)!}
            pois={pois}
            tags={tags}
            isReadOnly={isReadOnly()}
            onShowOnMap={onShowOnMap}
          />
        ))}

        {/* Footer row (sticky bottom): where you sleep tonight */}
        <div className="sticky bottom-0 z-20 bg-page" />
        {dayViews.map((dayView) => (
          <div
            key={dayView.date}
            className="sticky bottom-0 z-20 bg-page pb-3 pt-2"
          >
            <AccommodationBanner
              date={dayView.date}
              accommodation={accommodationByDate.get(dayView.date)!}
              isReadOnly={isReadOnly()}
              onShowOnMap={onShowOnMap}
            />
          </div>
        ))}

        {/* Sticky elements can't outrun their parent, so the gutter lives in
            an out-of-flow overlay spanning the full body row rather than in
            the 56px column cell; `inset-0` stretches it to that grid area. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{ gridRow: 2, gridColumn: "1 / -1" }}
          aria-hidden
        >
          <TimeAxis heightPx={maxColumnHeight} now={nowMarker} />
        </div>

        {/* Same trick across header, body and footer rows so zoom pins to the
            top-left corner on both axes. Rows are implicit, so `-1` would
            only reach row 1. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{ gridRow: "1 / 4", gridColumn: "1 / -1" }}
        >
          <div
            className="pointer-events-auto sticky left-0 top-0 z-30 flex w-14 items-center justify-center gap-0.5 bg-page pb-2 pt-3"
            role="group"
            aria-label="Calendar zoom"
          >
            <Button
              variant="icon"
              size="sm"
              icon="zoom_out"
              aria-label="Zoom out"
              title="Zoom out"
              disabled={zoomIndex <= 0}
              onClick={zoomCalendarOut}
            />
            <Button
              variant="icon"
              size="sm"
              icon="zoom_in"
              aria-label="Zoom in"
              title="Zoom in"
              disabled={zoomIndex >= ZOOM_LEVELS_PX_PER_HOUR.length - 1}
              onClick={zoomCalendarIn}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
